import { useRef, useState } from "react";
import {
  FaChevronLeft,
  FaChevronRight,
  FaLock,
  FaRegCheckCircle,
  FaSearch,
} from "react-icons/fa";
import { FiCpu } from "react-icons/fi";
import { IoCodeSharp } from "react-icons/io5";
import { effieSystem } from "./data";
import { effieSystemStyles } from "./style/style";
const EffieSystem = () => {
  const [activeTab, setActiveTab] = useState("orchestrator");
  const [isAnimating, setIsAnimating] = useState(false);

  const handleTabChange = (id) => {
    if (activeTab === id) return;
    setIsAnimating(true);
    setTimeout(() => {
      setActiveTab(id);
      setIsAnimating(false);
    }, 300);
  };

  const scrollRef = useRef(null);

  const keys = Object.keys(effieSystem);

  const handleScroll = () => {
    const container = scrollRef.current;
    if (!container) return;

    const children = Array.from(container.children);
    const containerCenter = container.scrollLeft + container.offsetWidth / 2;

    let closestIndex = 0;
    let closestDistance = Infinity;

    children.forEach((child, index) => {
      const childCenter = child.offsetLeft + child.offsetWidth / 2;

      const distance = Math.abs(containerCenter - childCenter);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    const newKey = keys[closestIndex];

    if (newKey !== activeTab) {
      handleTabChange(newKey);
    }
  };

  const moveCarousel = (direction) => {
    const container = scrollRef.current;
    if (!container) return;

    const cardWidth = container.firstChild.offsetWidth + 12;
    const maxScroll = container.scrollWidth - container.offsetWidth;

    let nextScroll = container.scrollLeft + direction * cardWidth;

    // Clamp — prevents going before first or after last
    nextScroll = Math.max(0, Math.min(nextScroll, maxScroll));

    container.scrollTo({
      left: nextScroll,
      behavior: "smooth",
    });
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 font-sans p-4 md:p-8 flex flex-col md:flex-row gap-6 md:gap-12 items-center justify-center overflow-hidden">
      <style>{effieSystemStyles}</style>

      {/* --- LEFT COLUMN: NAVIGATION & TEXT --- */}
      <div className="w-full md:w-1/3 flex flex-col z-20 order-2 md:order-1 mt-6">
        {/* Branding */}

        {/* Mobile: Horizontal Scroll Nav */}

        <div className="relative">
          <button
            onClick={() => moveCarousel(-1)}
            className="md:hidden absolute left-0 top-1/2 -translate-y-1/2 z-30
               bg-slate-900/80 backdrop-blur border border-slate-700
               p-2 rounded-full"
          >
            <FaChevronLeft />
          </button>

          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="flex md:flex-col overflow-x-auto md:overflow-visible gap-3 md:pb-0 
            no-scrollbar snap-x"
          >
            {Object.entries(effieSystem).map(([key, item]) => (
              <button
                key={key}
                onClick={() => handleTabChange(key)}
                className={`
                snap-center shrink-0 w-[85vw] md:w-full text-left p-4 rounded-xl border transition-all duration-300 relative overflow-hidden group
                ${
                  activeTab === key
                    ? `${item.bg} ${item.border} shadow-[0_0_20px_rgba(0,0,0,0.3)]`
                    : "bg-slate-900 border-slate-800 hover:border-slate-700"
                }
              `}
              >
                {/* Active Glow Bar */}
                {activeTab === key && (
                  <div
                    className={`absolute left-0 top-0 bottom-0 w-1 ${item.color.replace(
                      "text",
                      "bg",
                    )}`}
                  />
                )}

                <div className="flex items-start gap-4">
                  <div
                    className={`mt-1 p-2 rounded-lg bg-slate-950 ${
                      activeTab === key ? item.color : "text-slate-500"
                    }`}
                  >
                    {item.icon}
                  </div>
                  <div>
                    <h3
                      className={`font-bold text-lg ${
                        activeTab === key ? "text-white" : "text-slate-400"
                      }`}
                    >
                      {item.title}
                    </h3>
                    <p
                      className={`text-xs uppercase tracking-wider font-semibold ${item.color} mb-2`}
                    >
                      {item.subtitle}
                    </p>

                    {/* Expanded Content (Only show on active desktop or always on mobile card) */}
                    <div
                      className={`transition-all duration-500 grid ${
                        activeTab === key
                          ? "grid-rows-[1fr] opacity-100 mt-2"
                          : "grid-rows-[0fr] opacity-0"
                      }`}
                    >
                      <div className="overflow-hidden">
                        <p className="text-sm text-slate-300 leading-relaxed mb-3">
                          {item.description}
                        </p>
                        <ul className="space-y-1">
                          {item.details.map((detail, i) => (
                            <li
                              key={i}
                              className="flex items-center gap-2 text-xs text-slate-400"
                            >
                              <FaRegCheckCircle
                                size={12}
                                className={item.color}
                              />{" "}
                              {detail}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Tech Spec Footer */}
          <div className="mt-8 pt-6 border-t  border-slate-800 hidden md:block">
            <h4 className="text-xs font-bold text-slate-500 uppercase mb-2">
              Technical Implementation
            </h4>
            <div className="flex gap-2 flex-wrap justify-center">
              {["React Flow", "LangGraph", "SSE Streaming", "Vector DB"].map(
                (tag) => (
                  <span
                    key={tag}
                    className="px-2 py-1 bg-slate-900 border border-slate-800 rounded text-[10px] text-slate-400 font-mono"
                  >
                    {tag}
                  </span>
                ),
              )}
            </div>
          </div>

          <button
            onClick={() => moveCarousel(1)}
            className="md:hidden absolute right-0 top-1/2 -translate-y-1/2 z-30
               bg-slate-900/80 backdrop-blur border border-slate-700
               p-2 rounded-full"
          >
            <FaChevronRight />
          </button>
        </div>
      </div>

      {/* --- RIGHT COLUMN: VISUALIZER STAGE --- */}
      <div className="w-full md:w-2/3 h-[400px] md:h-[600px] bg-slate-900/50 rounded-3xl border border-slate-800 relative flex items-center justify-center order-1 md:order-2 overflow-hidden shadow-2xl">
        {/* Background Grid */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_at_center,black,transparent_80%)]"></div>

        {/* THE VISUAL CORE */}
        <div
          className={`relative w-full h-full flex items-center justify-center transition-opacity duration-300 ${
            isAnimating
              ? "opacity-0 scale-90 blur-sm"
              : "opacity-100 scale-100 blur-0"
          }`}
        >
          {/* 1. VISUAL: ORCHESTRATOR (The Brain) */}
          {activeTab === "orchestrator" && (
            <div className="relative flex items-center justify-center w-full h-[320px] sm:h-[400px] md:h-[500px]">
              {/* Wrapper */}
              <div className="relative scale-[0.85] sm:scale-95 md:scale-100">
                {/* Central Brain Node */}
                <div
                  className="relative z-10
        w-28 h-28 sm:w-30 sm:h-30 md:w-32 md:h-32
        rounded-full bg-slate-950 border-2 border-rose-500
        flex items-center justify-center
        shadow-[0_0_50px_rgba(244,63,94,0.4)]
        animate-[float_4s_ease-in-out_infinite]"
                >
                  <FiCpu size={36} className="text-rose-500 sm:hidden" />
                  <FiCpu
                    size={42}
                    className="text-rose-500 hidden sm:block md:hidden"
                  />
                  <FiCpu size={48} className="text-rose-500 hidden md:block" />

                  <div className="absolute inset-0 border-2 border-rose-500 rounded-full animate-[pulse-ring_2s_infinite]" />
                </div>

                {/* Satellites */}
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="absolute
          w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12
          bg-slate-900 border border-rose-500/50 rounded-lg
          flex items-center justify-center"
                    style={{
                      top: "50%",
                      left: "50%",
                      transform: `rotate(${i * 120}deg) translate(
              clamp(105px, 22vw, 140px)
            ) rotate(-${i * 120}deg)`,
                    }}
                  >
                    <FaSearch size={15} className="text-rose-400 sm:hidden" />
                    <FaSearch
                      size={16}
                      className="text-rose-400 hidden sm:block"
                    />

                    {/* Connecting Wire */}
                    <div
                      className="absolute top-1/2 left-1/2
            w-[clamp(105px,22vw,140px)] h-[2px]
            bg-gradient-to-r from-rose-500 to-transparent
            origin-left -z-10"
                      style={{
                        transform: `rotate(${i * 120 + 180}deg)`,
                      }}
                    />
                  </div>
                ))}

                {/* Status */}
                <div
                  className="absolute -bottom-12 sm:-bottom-16 w-full text-center
      text-rose-400 font-mono text-[11px] sm:text-xs animate-pulse"
                >
                  State: DELEGATING_TASKS
                </div>
              </div>
            </div>
          )}

          {/* 2. VISUAL: MCP HUB (The Plug) */}
          {activeTab === "mcp" && (
            <div className="relative w-64 h-64">
              {/* Central Hub */}
              <div className="absolute inset-0 flex items-center justify-center z-20">
                <div className="w-24 h-24 bg-slate-950 border-2 border-cyan-400 rounded-xl flex items-center justify-center shadow-[0_0_40px_rgba(34,211,238,0.3)]">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="w-2 h-2 bg-cyan-400 rounded-full"></div>
                    <div className="w-2 h-2 bg-cyan-400 rounded-full"></div>
                    <div className="w-2 h-2 bg-cyan-400 rounded-full"></div>
                    <div className="w-2 h-2 bg-cyan-400 rounded-full"></div>
                  </div>
                </div>
              </div>

              {/* Orbiting Plugs */}
              <div className="absolute inset-0 animate-[orbit_10s_linear_infinite]">
                {["GitHub", "Drive", "Slack", "SQL"].map((label, i) => (
                  <div
                    key={i}
                    className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-6 flex flex-col items-center"
                    style={{
                      transform: `rotate(${i * 90}deg) translateY(0px)`,
                    }}
                  >
                    <div className="w-12 h-16 bg-slate-800 border border-cyan-500/50 rounded-b-lg flex items-end justify-center pb-2 mb-2">
                      <div className="w-1 h-3 bg-cyan-500"></div>
                    </div>
                  </div>
                ))}
              </div>
              {/* Context Beams */}
              <div className="absolute inset-0 border border-cyan-500/20 rounded-full animate-ping"></div>
            </div>
          )}

          {/* 3. VISUAL: CRITIC LOOP (The Cycle) */}
          {activeTab === "critic" && (
            <div className="relative w-80 h-40 flex items-center justify-between">
              {/* Agent B (Coder) */}
              <div className="relative w-20 h-20 bg-slate-900 border border-purple-500 rounded-full flex items-center justify-center z-10">
                <IoCodeSharp className="text-purple-400" />
                <span className="absolute -bottom-6 text-[10px] text-purple-300">
                  Generator
                </span>
              </div>

              {/* Agent C (Critic) */}
              <div className="relative w-20 h-20 bg-slate-900 border border-purple-500 rounded-full flex items-center justify-center z-10">
                <FaSearch className="text-purple-400" />
                <span className="absolute -bottom-6 text-[10px] text-purple-300">
                  Critic
                </span>
              </div>

              {/* Animated SVG Path Loop */}
              <svg className="absolute inset-0 w-full h-full pointer-events-none overflow-visible">
                <path
                  d="M 60 20 C 120 -20, 200 -20, 260 20"
                  fill="none"
                  stroke="#a855f7"
                  strokeWidth="2"
                  strokeDasharray="5 5"
                  className="animate-[dash-flow_1s_linear_infinite]"
                />
                <path
                  d="M 260 60 C 200 100, 120 100, 60 60"
                  fill="none"
                  stroke="#a855f7"
                  strokeWidth="2"
                  strokeDasharray="5 5"
                  className="animate-[dash-flow_1s_linear_infinite]"
                  style={{ animationDirection: "reverse" }}
                />

                {/* Feedback Badge */}
                <foreignObject x="120" y="30" width="80" height="30">
                  <div className="bg-red-500 text-white text-[10px] font-bold px-2 py-1 rounded text-center animate-bounce">
                    Rejection!
                  </div>
                </foreignObject>
              </svg>
            </div>
          )}

          {/* 4. VISUAL: IP SENTINEL (The Shield) */}
          {activeTab === "sentinel" && (
            <div className="relative flex flex-col items-center">
              {/* Data Stream Input */}
              <div className="w-40 h-24 bg-slate-800 border border-slate-600 rounded mb-4 overflow-hidden relative">
                <div className="p-2 text-[8px] font-mono text-slate-400 opacity-50">
                  User: John Doe
                  <br />
                  ID: 994-221
                  <br />
                  SSN: ***-**-****
                  <br />
                  Project: Omega
                </div>
                {/* Scanner Line */}
                <div className="absolute left-0 w-full h-[2px] bg-emerald-400 shadow-[0_0_15px_#34d399] animate-[scan-vertical_2s_linear_infinite]"></div>
              </div>

              {/* Shield Icon */}
              <div className="w-24 h-24 bg-slate-950 border-4 border-emerald-500 rounded-full flex items-center justify-center shadow-[0_0_50px_rgba(16,185,129,0.3)] z-10 relative">
                <FaLock size={32} className="text-emerald-500" />
                <div className="absolute -right-12 top-0 bg-emerald-900 text-emerald-300 text-[10px] px-2 py-1 rounded border border-emerald-500">
                  PII Redacted
                </div>
              </div>

              {/* Safe Output */}
              <div className="mt-4 w-40 h-16 bg-emerald-900/20 border border-emerald-500/30 rounded flex items-center justify-center text-emerald-400 font-mono text-xs">
                Clean Data Stream
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EffieSystem;
